<template>
  <base-dialog-button :isOpen="dialogOpen" @closeDialog="toggleDialog" :size="mobileVersion ? 'fs' : 'lg-fix-h'">
    <template slot="header">
      <div class="h-16 bg-secondary flex items-center justify-between text-white px-4">
        <div class="flex items-center">
          <h2 class="font-bold ml-2">{{ $t('invoices.dialogs.correct_invoice_header') }}</h2>
        </div>
        <span class="cursor-pointer" @click="toggleDialog">
          <base-button-icon name="cross" size="xs" iconSize="sm" mode="secondary" />
        </span>
      </div>
    </template>
    <template slot="main">
      <div class="form-wrapper p-4 overflow-x-auto"
        :style="mobileVersion ? 'height: 80vh;' : 'height: 60vh;'">
        <el-form ref="ruleForm" class="h-full" :model="form" :rules="rules">
          <div class="lg:mb-2 flex gap-2" :class="{ '!block': !isLower1650 }">
            <div class="w-full" :class="{ 'mb-2': !isLower1650 }">
              <el-form-item prop="invoiceNumber" class="mb-0">
                <label
                  class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon />{{ $t('invoices.num_factura') }}</label>
                <el-input v-model="form.invoiceNumber" disabled>
                  <template v-if="form.invoiceSerial" #prepend>{{ form.invoiceSerial }} / </template>
                </el-input>
              </el-form-item>
            </div>
            <div class="w-full">
              <el-form-item prop="issueDate" class="mb-0">
                <label
                  class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon />{{ $t('invoices.fecha_emision') }}</label>
                <el-date-picker v-model="form.issueDate" type="date" :picker-options="dateRangeOptions"
                  popper-class="!z-[2020]" :format="datePattern" value-format="yyyy-MM-dd[T00:00:00]" :disabled="loading" />
              </el-form-item>
            </div>
          </div>
          <div class="lg:mb-2">
            <div class="flex gap-2">
              <div class="w-full">
                <el-form-item prop="fiscalPeriodFrom" class="mb-0">
                  <div class="flex items-center mb-1">
                    <label class="text-sm font-semibold text-secondary-600 block">{{ $t('invoices.periodo_fiscal') }} {{ $t('app.from').toLowerCase() }}</label>
                    <base-tooltip-icon-multiple size="sm" :content="periodoFiscalTooltip" class="ml-2" />
                  </div>
                  <el-date-picker v-model="form.fiscalPeriodFrom" type="date" :picker-options="dateRangeOptions"
                    popper-class="!z-[2020]" :format="datePattern" value-format="yyyy-MM-dd[T00:00:00]" :disabled="loading" />
                </el-form-item>
              </div>
              <div class="w-full">
                <el-form-item prop="fiscalPeriodTo" class="mb-0">
                  <div class="flex items-center mb-1">
                    <label class="text-sm font-semibold text-secondary-600 block">{{ $t('invoices.periodo_fiscal') }} {{ $t('app.to').toLowerCase() }}</label>
                    <base-tooltip-icon-multiple size="sm" :content="periodoFiscalTooltip" class="ml-2" />
                  </div>
                  <el-date-picker v-model="form.fiscalPeriodTo" type="date" :picker-options="dateRangeOptions"
                    popper-class="!z-[2020]" :format="datePattern" value-format="yyyy-MM-dd[T00:00:00]" :disabled="loading" />
                </el-form-item>
              </div>
            </div>
          </div>
          <div class="lg:mb-2 block lg:flex flex-row gap-2">
            <div class="w-full">
              <el-form-item prop="files" ref="filesRef" class="max-w-md mb-0">
                <label class="text-sm font-semibold text-secondary-600 block">{{ $t('invoices.pdf_invoice') }}</label>
                <div @dragenter="$event.preventDefault()" @dragover="$event.preventDefault()" @drop.prevent.stop="onFileDrop">
                  <Upload ref="upload" id="fileSelector" class="bg-white dark:bg-darkblue-900"
                    :class="[isLower1024 ? 'custom-upload-cert' : 'custom-upload ']" accept="application/pdf"
                    drag action="#" :auto-upload="false" :on-remove="handleRemove" :limit="maxFiles"
                    :on-change="handleChange" :on-exceed="handleOnExceed" :file-list="files" :disabled="loading">
                    <i class="el-icon-upload"></i>
                    <p v-if="files.length" slot="default" class="el-upload__text"><em>{{ this.fileName }}</em></p>
                    <div v-else slot="default" class="el-upload__text" v-html="$tc('app.upload_default_no_file_text', 1)"></div>
                    <upload-file-item slot="file" slot-scope="{file}" :file="file" hide-expand-action custom="!max-h-fit" @remove="handleRemove" reverse-colors />
                  </Upload>
                </div>
              </el-form-item>
            </div>
            <div class="w-full">
              <span
                class="text-sm font-semibold text-secondary-600 block mb-1"></span>
              <el-switch class="switch-text break-normal" v-model="form.sendAuto" :active-text="$t('app.send_auto')" :disabled="loading"></el-switch>
            </div>
          </div>
        </el-form>
      </div>
    </template>
    <template slot="footer">
      <div class="flex flex-row w-full justify-between">
        <base-button class="w-full mx-2" mode="secondary-phantom-blue" :text="$t('app.cancel')" :loading="loading" @click="toggleDialog" />
        <base-button mode="secondary-phantom-blue" class="w-full mx-2" :loading="loading"
          :text="$tc('invoices.dialogs.correct_invoice', 1)" @click="applyDialog('ruleForm')" />
      </div>
    </template>
  </base-dialog-button>
</template>
<script>
import moment from 'moment'
import { i18n } from '@/i18n'
import getLocaleDateTimeFormat from '@/services/functions/getLocaleDateTimeFormat'
import { emitirFacturaRectificativa } from '@/services/invoices/emitirFacturaRectificativa'
import { getNextInvoiceRectificative } from '@/services/invoices/getNextInvoiceRectificative'

import { Upload } from 'element-ui'
import UploadFileItem from '@/components/UploadFileItem.vue'

export default {
  name: 'invoice-correction-dialog',
  components: {
    Upload,
    UploadFileItem
  },
  props: {
    dialogOpen: {
      type: Boolean,
      default: false
    },
    mobileVersion: {
      type: Boolean,
      default: false
    },
    idFacturae: [Number, String],
    idInvoice: [Number, String]
  },
  data () {
    return {
      dateRangeOptions: {
        firstDayOfWeek: 1,
      },
      loading: false,
      isOpen: this.dialogOpen,
      files: [],
      form: this.getDefaultForm(),
      periodoFiscalTooltip: [i18n.t('tooltip.rectificative_fiscal_period_header_info'), i18n.t('tooltip.rectificative_fiscal_period_info')],
      rules: {
        invoiceNumber: [{
          required: true,
          message: i18n.t('validation.complete'),
          trigger: ['blur', 'change'],
        }],
        invoiceSerial: [{
          required: true,
          message: i18n.t('validation.complete'),
          trigger: ['blur', 'change'],
        }],
        issueDate: [{
          required: true,
          message: i18n.t('validation.complete'),
          trigger: ['blur', 'change'],
        }],
        fiscalPeriodFrom: [{
          required: false,
          message: i18n.t('validation.complete'),
          trigger: ['blur', 'change'],
        },
        {
          required: true,
          trigger: ['blur', 'change'],
          validator: (rule, value, callback) => {
            if (!this.form.fiscalPeriodTo && Date.parse(value) >= Date.parse(this.form.fiscalPeriodTo)) {
              return callback(new Error(i18n.t('validation.fiscal_period_from')))
            }
            return callback()
          }
        }],
        fiscalPeriodTo: [{
          required: false,
          message: i18n.t('validation.complete'),
          trigger: ['blur', 'change'],
        },
        {
          required: true,
          trigger: ['blur', 'change'],
          validator: (rule, value, callback) => {
            if (!this.form.fiscalPeriodFrom && Date.parse(value) <= Date.parse(this.form.fiscalPeriodFrom)) {
              return callback(new Error(i18n.t('validation.fiscal_period_to')))
            }
            return callback()
          }
        }],
        file: [{
          required: true,
          trigger: ['blur', 'change'],
          validator: (rule, value, callback) => {
            if (!this.files?.length && !this.hasXsltModule) {
              return callback(new Error(i18n.t('validation.complete')))
            }
            if (this.files.length > this.maxFiles) {
              return callback(new Error(i18n.t('validation.max_files', { maxFilesAdj: this.maxFiles })))
            }
            return callback()
          }
        },
        {
          required: true,
          trigger: ['blur', 'change'],
          validator: (rule, value, callback) => {
            if (this.files?.every(file => ['PDF'].includes(this.getExtensionFromFileName(file).toUpperCase()))) {
              return callback(new Error(i18n.t('validation.not_single_valid_extension', { ext: '.pdf' })))
            }
            callback()
          }
        },
        {
          required: true,
          trigger: ['blur', 'change'],
          validator: (rule, value, callback) => {
            let totalSize = 0
            this.files.forEach(file => {
              totalSize = totalSize + file.size
            })
            if (totalSize > this.maxSizeFile) {
              return callback(new Error(i18n.t('validation.exceed_size_files_total') + ' (' + this.maxMBAdj + ' MB)'))
            }
            callback()
          }
        }]
      },
      maxFiles: 1,
      maxSizeFile: 7340032, // Bytes (7MB)
    }
  },
  computed: {
    datePattern () {
      const formatKey = 'numeric_date'
      const locale = i18n.locale || 'es'
      return getLocaleDateTimeFormat(formatKey, locale)
    },
    hasXsltModule: function () {
      return this.$store.getters['auth/hasXsltModule']
    },
    isLower1024: function () {
      return this.$store.getters['app/getIsLower1024']
    },
    isLower1650: function () {
      return this.$store.getters['app/getIsLower1650']
    },
    fileName: function () {
      if (this.files?.length) {
        return this.files[0].name
      }
      return ''
    }
  },
  watch: {
    // Inicializar al abrir el componente
    dialogOpen: async function (value, oldValue) {
      if (value) {
        this.resetForm()
        const next = await this.getNextInvoiceRectificative()
        this.setNext(next)
      }
    },
  },
  methods: {
    getDefaultForm: function () {
      return {
        invoiceNumber: '',
        invoiceSerial: '',
        issueDate: '',
        fiscalPeriodFrom: '',
        fiscalPeriodTo: '',
        sendAuto: false
      }
    },
    resetForm: async function () {
      this.form = this.getDefaultForm()
    },
    setNext: function (next) {
      if (next) {
        this.form.invoiceNumber = next.InvoiceNumber || ''
        this.form.invoiceSerial = next.SerialNumber || 'R'
        this.form.issueDate = next.issueDate || moment().format('yyyy-MM-DD[T00:00:00]')
      }
    },
    toggleDialog: function () {
      if (!this.loading) {
        this.isOpen = !this.isOpen
        this.resetForm()
        this.$emit('toggleDialog')
      }
    },
    applyDialog: async function (formName) {
      if (!this.loading) {
        this.$refs[formName].validate(async (valid) => {
          try {
            if (valid) {
              this.loading = true
              const payload = {
                idFacturae: this.idFacturae || null,
                idInvoice: this.idInvoice || null,
                invoiceNumber: this.form.invoiceNumber || null,
                invoiceIssueDate: this.form.issueDate || null,
                periodoTributarioStart: this.form.fiscalPeriodFrom || null,
                periodoTributarioEnd: this.form.fiscalPeriodTo || null,
                autoSend: !!this.form.sendAuto
              }
              const response = await emitirFacturaRectificativa(this.files, payload)
              if (response.blnError) {
                this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
              } else {
                this.doFeedback(i18n.t('feedback.success.entity_updated_msg'), 'success')
                this.$emit('applyDialog')
              }
            }
          } finally {
            this.loading = false
          }
        })
      }
    },
    onFileDrop (event) {
      if (!this.loading) {
        [...event.dataTransfer.files].filter(file => !file.uid).forEach(file => this.handleChange({ name: file.name, raw: file, status: 'ready' }))
      }
    },
    handleChange: function (archivo) {
      this.$refs.filesRef.clearValidate()

      const extension = this.getExtensionFromFileName(archivo.name).toUpperCase()
      var error = false
      if (extension !== 'PDF') {
        error = i18n.t('validation.not_single_valid_extension', { ext: '.pdf' })
      }
      if (error && error !== '') {
        this.doFeedback(error)
        this.handleRemove(archivo)
      } else {
        this.files.push(archivo)
      }
    },
    handleOnExceed: function () {
      this.doFeedback(i18n.t('validation.max_files_warn', { limit: this.maxFilesAdj }), 'warning')
    },
    handleRemove: function (file) {
      this.files = this.files.filter(x => x.uid !== file.uid)
    },
    getExtensionFromFileName: function (fileName) {
      return fileName.split('.').at(-1)
    },
    getNextInvoiceRectificative: async function () {
      try {
        this.loading = true
        const payload = {
          idFacturae: this.idFacturae, 
          idInvoice: this.idInvoice
        }
        const response = await getNextInvoiceRectificative(payload)
        if (response.blnError) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
          return null
        }
        return response.respuesta || null
      } finally {
        this.loading = false
      }
    }
  },
}
</script>
<style scoped>
.el-date-editor {
  width: 100%;
}
.el-form-item:has(.el-upload)::v-deep .el-form-item__content {
  line-height: inherit;
}
#fileSelector::v-deep .el-upload:has(+ .is-disabled),
#fileSelector::v-deep .el-upload:has(+ .is-disabled) .el-upload-dragger {
  cursor: not-allowed;
}
</style>
